import { useState, useEffect, useCallback, useMemo } from 'react';
import { Organization } from '../../../shared/types';
import { UsersIcon, EnvelopeIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../../utils/api';
import Loading from '../../Loading';
import SubmitButton from 'src/components/SubmitButton';
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from '../../table/StaticDataTable';
import ActionsMenu from '../../ActionsMenu';
import TableBadge from '../../table/TableBadge';
import TableDateTime, { getDateTimeSortValue } from '../../table/TableDateTime';
import { SortDirection } from '../../table/useTableData';
import { Roles } from 'src/shared/abilities';
import { useAuth } from 'src/contexts/AuthContext';

const memberColumns: StaticDataColumnProps[] = [
  { title: 'User', sortable: true },
  { title: 'Verified at', sortable: true, defaultSort: true, defaultSortDirection: SortDirection.DESC },
  { title: 'Role', sortable: true },
];

type MemberRoles = Exclude<Roles, Roles.MASTER_ADMIN>;
interface OrganizationMember {
  id: number;
  email: string;
  role: MemberRoles;
  created_at: string;
  email_verified_at: string | null;
}

const memberToRows = (
  member: OrganizationMember,
  onUpdateRole: (userId: number, newRole: MemberRoles) => void,
  onRemoveUser: (userId: number) => void,
  onInviteUser: (email: string, role: MemberRoles) => void,
  currentUserId: number | undefined
): StaticDataRow[] => {
  const emailedAt = member.email_verified_at ? new Date(member.email_verified_at) : null;
  const emailedDate = emailedAt ? emailedAt.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : null;
  const emailedTime = emailedAt ? emailedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : null;

  const verifiedActions = [
    {
      id: 'set-org-admin',
      label: 'Set as Admin',
      onClick: () => onUpdateRole(member.id, Roles.ORG_ADMIN),
      disabled: member.role === Roles.ORG_ADMIN,
    },
    {
      id: 'set-basic-user',
      label: 'Set as Basic User',
      onClick: () => onUpdateRole(member.id, Roles.BASIC_USER),
      disabled: member.role === Roles.BASIC_USER,
    },
  ];

  const unverifiedActions = [
    {
      id: 'resend-invite',
      label: 'Resend Invite',
      onClick: () => onInviteUser(member.email, member.role),
    },
  ];

  return [
    {
      name: 'User',
      value: member.email,
      element: <span className="text-gray-900">{member.email}</span>
    },
    {
      name: 'Role',
      value: member.role,
      element: (
        <TableBadge
          text={member.role === Roles.ORG_ADMIN ? 'Organization Admin' : 'Basic User'}
          bgColorClass={member.role === Roles.ORG_ADMIN ? 'bg-purple-500' : 'bg-gray-400'}
        />
      ),
    },
    {
      name: 'Verified at',
      value: getDateTimeSortValue(emailedDate, emailedTime),
      element: <TableDateTime date={emailedDate} startTime={emailedTime} />,
    },
    {
      name: 'Actions',
      value: '',
      element: (
        <ActionsMenu
          actions={member.id === currentUserId ? [] : [
            ...(member.email_verified_at ? verifiedActions : unverifiedActions),
            {
              id: 'remove',
              label: 'Remove from organization',
              onClick: () => onRemoveUser(member.id),
            },
          ]}
        />
      ),
    },
  ];
};

interface OrganizationUserManagementTabProps {
  organization: Organization;
}

const OrganizationUserManagementTab = ({ organization }: OrganizationUserManagementTabProps) => {
  const { user } = useAuth();
  const [ members, setMembers ] = useState<OrganizationMember[]>([]);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ isInviting, setIsInviting ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  const [ success, setSuccess ] = useState<string | null>(null);
  const [ newUserEmail, setNewUserEmail ] = useState('');
  const [ newUserRole, setNewUserRole ] = useState<MemberRoles>(Roles.BASIC_USER);
  const [ allowedDomains, setAllowedDomains ] = useState<string[]>([]);

  const fetchMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiGet(`/api/organizations/${organization.id}/members`);
      const data = await response.json();
      if (!data.error) {
        setMembers(data);
        setError(null);
      } else {
        setError(data.error);
      }
    } catch (error) {
      setError('Error fetching organization members');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [ organization.id ]);

  const fetchAllowedDomains = useCallback(async () => {
    try {
      const response = await apiGet(`/api/organizations/${organization.id}/allowed-domains`);
      const data = await response.json();
      setAllowedDomains(data.allowed_domains || []);
    } catch (error) {
      console.error("Error fetching allowed domains:", error);
    }
  }, [ organization.id ]);

  useEffect(() => {
    fetchMembers();
    fetchAllowedDomains();
  }, [ fetchMembers, fetchAllowedDomains ]);

  const handleInviteUser = useCallback(async (email: string, role: MemberRoles) => {
    try {
      setIsInviting(true);

      const emailDomain = email.split('@')[1]?.toLowerCase();
      if (!emailDomain || !allowedDomains.some(domain => domain.toLowerCase() === emailDomain)) {
        setError(`Invalid email domain.`);
        setIsInviting(false);
        return;
      }

      const response = await apiPost(`/api/organizations/${organization.id}/invite`, { email, role });
      const data = await response.json();
      if (data.error) {
        setError(data.error);
        return;
      }

      fetchMembers();
      setNewUserEmail('');
      setNewUserRole(Roles.BASIC_USER);
      setSuccess(data.message || `Invitation sent to ${email}`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (error) {
      setError('Error sending invite');
      console.error('Error:', error);
    } finally {
      setIsInviting(false);
    }
  }, [ organization.id, fetchMembers, allowedDomains ]);

  const handleUpdateRole = useCallback(async (userId: number, newRole: MemberRoles) => {
    try {
      const user = members.find((member) => member.id === userId);
      if (!user) { return; }
      const { email } = user;
      const response = await apiPatch(`/api/organizations/${organization.id}/members`, { role: newRole, userEmail: email });
      const data = await response.json();
      if (data.error) {
        setError(data.error);
        return;
      }
      setSuccess(data.message || 'User role updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchMembers();
    } catch (error) {
      setError('Error updating user role');
      console.error('Error:', error);
    }
  }, [ organization.id, members, fetchMembers ]);

  const handleRemoveUser = useCallback(async (userId: number) => {
    if (!window.confirm('Are you sure you want to remove this user from the organization?')) { return; }
    try {
      const user = members.find((member) => member.id === userId);
      if (!user) { return; }
      const { email } = user;
      const response = await apiDelete(`/api/organizations/${organization.id}/members`, { userEmail: email });
      const data = await response.json();
      if (data.error) {
        setError(data.error);
        return;
      }
      fetchMembers();
      setSuccess(data.message || 'User removed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError('Error removing user');
      console.error('Error:', error);
    }
  }, [ organization.id, members, fetchMembers ]);

  const rows = useMemo(
    () => members.map((m) => memberToRows(m, handleUpdateRole, handleRemoveUser, handleInviteUser, user?.id)),
    [ members, handleUpdateRole, handleRemoveUser, handleInviteUser, user?.id ]
  );

  if (isLoading) return (<Loading message="Members" />);

  return (
    <div className="space-y-6">
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <div className="w-5 h-5 bg-green-400 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <div className="w-5 h-5 bg-red-400 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✕</span>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <EnvelopeIcon className="w-5 h-5" />
            Invite User
          </h3>
        </div>
        <div className="px-6 py-5 space-y-5">
          {allowedDomains.length === 0 ? (
            <p className="text-sm text-gray-500">No allowed domains configured. Add domains in General Settings to enable invites.</p>
          ) : (
            <div className="flex items-start gap-3 rounded-md bg-gray-50 border border-gray-200 px-4 py-3">
              <GlobeAltIcon className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Allowed domains</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {allowedDomains.map((domain) => (
                    <span
                      key={domain}
                      className="inline-flex items-center rounded-full bg-brand-50 border border-brand-200 px-2.5 py-0.5 text-xs font-medium text-brand-700"
                    >
                      {domain}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
          <form className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="Enter user's email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  id="role"
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value as MemberRoles)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value={Roles.BASIC_USER}>Basic User</option>
                  <option value={Roles.ORG_ADMIN}>Organization Admin</option>
                </select>
              </div>
            </div>
            <div>
              <SubmitButton
                loading={isInviting}
                loadingText="Sending Invite..."
                text="Send Invite"
                onClick={(e) => {
                  e.preventDefault();
                  handleInviteUser(newUserEmail, newUserRole);
                }}
              />
            </div>
          </form>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <UsersIcon className="w-5 h-5" />
            Organization Members ({members.length})
          </h3>
        </div>

        {members.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No members</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding the first user to your organization.
            </p>
          </div>
        ) : (
          <div className="-mx-2">
            <StaticDataTable
              tableId="org-members"
              columns={memberColumns}
              rows={rows}
              updateUrlParams={false}
              trailingColumnKey="Actions"
              rowId={(item: Record<string, unknown>) => String(item.id)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationUserManagementTab;
